import { useEffect, useRef, useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import { UMCheckbox, UMIcon, UMR, UMToolbarIconButton } from '../_components';
import light from '../_components/assets/basemap-light.jpg';
import streets from '../_components/assets/basemap-streets.jpg';
import satellitePure from './satellite-pure.png';
import satellite from '../_components/assets/basemap-satellite.jpg';
import outdoors from '../_components/assets/basemap-outdoors.jpg';
import type { BasemapId } from './types';

export const BASEMAPS: { id: BasemapId; label: string; thumbnail?: string }[] = [
  { id: 'light', label: 'Clair', thumbnail: light },
  { id: 'streets', label: 'Terrain', thumbnail: streets },
  { id: 'satellite', label: 'Satellite', thumbnail: satellite },
  { id: 'outdoors', label: 'Relief', thumbnail: outdoors },
];

interface Props {
  value: BasemapId;
  hasToken: boolean;
  onChange: (id: BasemapId) => void;
  labels: boolean;
  onLabelsChange: (checked: boolean) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  is3d: boolean;
  onToggle3d: (value: boolean) => void;
}

// Map thumbnails follow the measured Urban Map tile, not a generic form button.
function Tile({ id, onClick, expanded, disabled, selected, labels }: {
  id: BasemapId; onClick: () => void; expanded?: boolean; disabled?: boolean; selected?: boolean; labels: boolean;
}) {
  const base = BASEMAPS.find((item) => item.id === id)!;
  const option = id === 'satellite' && !labels ? { ...base, thumbnail: satellitePure } : base;
  return (
    <button
      type="button"
      aria-label={expanded === undefined ? option.label : `Choisir le fond de carte : ${option.label}`}
      aria-expanded={expanded}
      aria-controls={expanded === undefined ? undefined : 'satellite-basemap-options'}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: UMR.size.basemapTile, height: UMR.size.basemapTile,
        padding: 0, flexShrink: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'space-between',
        border: expanded === undefined ? 'none' : `1px solid ${UMR.border}`,
        background: UMR.white, color: UMR.black, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1, fontFamily: UMR.font,
      }}
    >
      {option.thumbnail
        ? <img src={option.thumbnail} alt="" style={{ width: '100%', flex: 1, minHeight: 0, objectFit: 'cover' }} />
        : <div style={{ flex: 1, display: 'grid', placeItems: 'center', width: '100%', background: UMR.grey100 }}>
          <UMIcon name="satellite_alt" size={32} color={UMR.blue} />
        </div>}
      <div style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: 29, flexShrink: 0, boxSizing: 'border-box', lineHeight: '21px',
        gap: 'var(--nj-semantic-size-spacing-4)', background: selected ? UMR.logoBg : undefined,
      }}>
        {selected && <UMIcon name="check_circle" family="icons" size={16} color="var(--nj-semantic-color-icon-status-success-primary-default)" />}
        <NJText as="span" style={{ fontFamily: UMR.font, fontSize: 14, lineHeight: '21px' }}>{option.label}</NJText>
      </div>
    </button>
  );
}

export function BasemapControls({
  value, hasToken, onChange, labels, onLabelsChange, onZoomIn, onZoomOut, is3d, onToggle3d,
}: Props) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLDivElement>(null);
  const closeAndFocus = () => {
    setOpen(false);
    window.requestAnimationFrame(() => trigger.current?.querySelector('button')?.focus());
  };
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  return (
    <div
      ref={container}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          closeAndFocus();
        }
      }}
      style={{
        position: 'absolute', bottom: UMR.size.controlsInset, left: UMR.size.controlsInset,
        right: UMR.size.controlsInset, display: 'flex', alignItems: 'flex-end',
        gap: 'var(--nj-semantic-size-spacing-8)', zIndex: 10, pointerEvents: 'none',
      }}
    >
      <div ref={trigger} style={{ pointerEvents: 'auto', display: open ? 'none' : undefined }}>
        <Tile id={value} labels={labels} expanded={open} onClick={() => setOpen((current) => !current)} />
      </div>
      {open && (
        <div
          id="satellite-basemap-options"
          role="group"
          aria-label="Fonds de carte disponibles"
          style={{
            minWidth: 0, display: 'flex', flexDirection: 'column',
            gap: 'var(--nj-semantic-size-spacing-8)', padding: 'var(--nj-semantic-size-spacing-8)',
            background: UMR.white, border: `1px solid ${UMR.border}`, pointerEvents: 'auto',
          }}
        >
          <NJText as="strong">Fond de carte</NJText>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--nj-semantic-size-spacing-8)', overflowX: 'auto' }}>
            {BASEMAPS.map((item) => <Tile key={item.id} id={item.id} labels={labels} selected={value === item.id}
              disabled={!hasToken && item.id !== 'light'}
              onClick={() => onChange(item.id)} />)}
            <UMToolbarIconButton icon="close" label="Fermer les fonds de carte" onClick={closeAndFocus} />
          </div>
          {value === 'satellite' && (
            <div style={{ borderTop: `1px solid ${UMR.border}`, paddingTop: 'var(--nj-semantic-size-spacing-8)' }}>
              <UMCheckbox checked={labels} disabled={!hasToken} label="Étiquettes" onChange={onLabelsChange} />
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexShrink: 0, background: UMR.white, border: `1px solid ${UMR.border}`, pointerEvents: 'auto' }}>
        <UMToolbarIconButton icon="zoom_out" label="Réduire le zoom" onClick={onZoomOut} />
        <UMToolbarIconButton icon="zoom_in" label="Augmenter le zoom" onClick={onZoomIn} />
      </div>
      <div style={{ display: 'flex', flexShrink: 0, background: UMR.white, border: `1px solid ${UMR.border}`, pointerEvents: 'auto' }}>
        <UMToolbarIconButton icon="2d_2" label={is3d ? 'Passer en 2D' : 'Vue 2D active'} onClick={() => onToggle3d(false)} />
        <UMToolbarIconButton icon="3d_2" label={is3d ? 'Vue 3D active' : 'Passer en 3D'} onClick={() => onToggle3d(true)} />
      </div>
    </div>
  );
}
